import { type ReactNode, useState } from 'react';

import {
  Button,
  ListRowTextGroup,
  Menu,
  MenuRoot,
  MenuRow,
  Text,
} from '@spotify-internal/encore-web';
import { Popover } from '@spotify-internal/encore-web/next';
import { MenuTriggerPlusIcon } from '@spotify/confidence-core-react';
import { useCheckResourcePermissions } from '@spotify/plugin-confidence-permissions-react';

import { type WorkflowExtensionOptions } from '../../extensions';
import { getWorkflowName } from '../../helpers';

const CreateExperimentMenuItem = ({
  ext,
  onSelect,
}: {
  ext: WorkflowExtensionOptions;
  onSelect: (ext: WorkflowExtensionOptions) => void;
}) => {
  const { allowed } = useCheckResourcePermissions({
    can: 'create_workflow_instance',
    name: getWorkflowName(ext.workflowId),
    optimistic: true,
  });

  const WorkflowIcon = ext.workflowIcon;
  const trackId = `menuitem.${ext.title.toLowerCase().replace(/\s+/g, '-')}`;

  return (
    <MenuRow
      size={ext.description ? 'large' : 'small'}
      textValue={ext.title}
      isDisabled={!allowed}
      data-track-id={trackId}
      data-track-action="update"
      onAction={() => {
        onSelect(ext);
      }}
    >
      <WorkflowIcon />
      <ListRowTextGroup>
        <Text variant="bodySmallBold">{ext.title}</Text>
        {ext.description && (
          <Text variant="bodySmall" semanticColor="textSubdued">
            {ext.description}
          </Text>
        )}
      </ListRowTextGroup>
    </MenuRow>
  );
};

type CreateExperimentMenuProps = {
  extensions: WorkflowExtensionOptions[];
};

export const CreateExperimentMenu = ({
  extensions,
}: CreateExperimentMenuProps): ReactNode => {
  const [createDialogExtension, setCreateDialogExtension] = useState<
    WorkflowExtensionOptions | undefined
  >();
  const CreateDialog = createDialogExtension?.createDialog;

  return (
    <>
      <MenuRoot>
        <Button variant="primary" size="small">
          <MenuTriggerPlusIcon />
          Create
        </Button>
        <Popover placement="bottomEnd">
          <Menu aria-label="Create experiment">
            {extensions.map(ext => (
              <CreateExperimentMenuItem
                key={ext.workflowId}
                ext={ext}
                onSelect={setCreateDialogExtension}
              />
            ))}
          </Menu>
        </Popover>
      </MenuRoot>
      {CreateDialog && (
        <CreateDialog
          open
          onClose={() => setCreateDialogExtension(undefined)}
        />
      )}
    </>
  );
};
